export type RouteName =
  | "landing"
  | "login"
  | "register"
  | "google-callback"
  | "referral"
  | "resend-verification"
  | "forgot-password"
  | "verify-email"
  | "reset-password"
  | "legal"
  | "checkout"
  | "member-overview"
  | "member-packages"
  | "member-upgrade"
  | "member-activity"
  | "member-affiliate"
  | "member-settings";

export const MEMBER_ROUTES = [
  "member-overview",
  "member-packages",
  "member-upgrade",
  "member-activity",
  "member-affiliate",
  "member-settings",
] as const satisfies readonly RouteName[];

export function isMemberRoute(route: RouteName): boolean {
  return route.startsWith("member-");
}

/**
 * Map pathname sang route. Bất kỳ đường dẫn nào không khớp đều rơi về `login`
 * (hoặc `member-overview` nếu nằm dưới /member), giống hành vi của bản trước.
 */
export function resolveRoute(pathname: string): RouteName {
  const path = pathname.replace(/\/+$/, "") || "/";
  // `/` là trang giới thiệu từ 09/10/2026; trước đó nó mở thẳng màn đăng nhập.
  if (path === "/") return "landing";
  // Store listing trỏ tới `/pricing`: mở trang giới thiệu, cuộn tới mục Giá.
  if (path === "/pricing") return "landing";
  if (path === "/login") return "login";
  if (path === "/register") return "register";
  // Google trả `id_token` về đây (redirect URI khai ở Google Cloud Console).
  if (path === "/auth/google") return "google-callback";
  // Link đối tác phát ra: `/r/<mã>`. Ngắn để đọc qua điện thoại được, và là chỗ
  // duy nhất đếm được lượt bấm — `?ref=` trên trang bất kỳ thì không.
  if (path.startsWith("/r/")) return "referral";
  if (path === "/resend-verification") return "resend-verification";
  if (path === "/forgot-password") return "forgot-password";
  if (path === "/verify-email") return "verify-email";
  if (path === "/reset-password") return "reset-password";
  // `/account/delete` là đường dẫn đã khai với Chrome Web Store là nơi người
  // dùng yêu cầu xoá dữ liệu. Không có route riêng thì nó rơi về trang đăng
  // nhập, và người duyệt — vốn không có tài khoản — chỉ thấy một ô mật khẩu.
  if (path === "/terms" || path === "/privacy" || path === "/support" || path === "/account/delete") return "legal";
  /*
    Trang trả tiền. Mã đơn nằm trong đường dẫn nên phải khớp theo tiền tố, không
    khớp đúng chuỗi.

    Đường dẫn `/thanh-toan/<mã>` giữ nguyên từ bản Jinja: extension đã phát hành
    mở đúng URL này, và người dùng có thể đang để dở một tab chờ chuyển khoản.
    Đổi nó là làm hỏng những đơn đang treo.
  */
  if (path.startsWith("/thanh-toan/")) return "checkout";
  if (path === "/member") return "member-overview";
  if (path === "/member/packages") return "member-packages";
  if (path === "/member/upgrade") return "member-upgrade";
  if (path === "/member/activity") return "member-activity";
  if (path === "/member/affiliate") return "member-affiliate";
  if (path === "/member/settings") return "member-settings";
  return path.startsWith("/member") ? "member-overview" : "login";
}

/** Mã đơn trong `/thanh-toan/<mã>`. Rỗng khi đường dẫn không phải trang này. */
export function checkoutOrderCode(pathname: string): string {
  const match = /^\/thanh-toan\/([^/?#]+)/.exec(pathname);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

export const MEMBER_PATHS: Record<(typeof MEMBER_ROUTES)[number], string> = {
  "member-overview": "/member",
  "member-packages": "/member/packages",
  "member-upgrade": "/member/upgrade",
  "member-activity": "/member/activity",
  "member-affiliate": "/member/affiliate",
  "member-settings": "/member/settings",
};
